import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Box, CircularProgress, Divider } from '@mui/material';

import { getBookingDataBySlug, BookingData } from '../services/bookingService';
import NavBar from '../components/NavBar';
import { BusinessAboutSection } from '../components/BusinessAboutSection';
import { BusinessContactSection } from '../components/BusinessContactSection';
import { BusinessBookSection } from '../components/BusinessBookSection';
import { useTheme } from '../contexts/ThemeContext';
import { BusinessAboutSectionOriginal } from '../components/BusinessAboutSectionOriginal';


export default function BookingPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const { applyThemeFromData } = useTheme();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const descriptionProvided = Boolean(bookingData?.businessDescription?.trim());
  const imagesProvided = Boolean(bookingData?.businessImages?.length);
  const logoProvided = Boolean(bookingData?.logoData);
  const newVersion = descriptionProvided || imagesProvided || logoProvided;

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 1. Load booking data after businessId is resolved
  useEffect(() => {
    async function loadBookingData() {
      if (!slug) return; // Type guard to ensure slug is defined
      
      try {
        const data = await getBookingDataBySlug(slug);
        setBookingData(data);
        
        // Apply theme from booking data (with fallback to default)
        applyThemeFromData(data.themeData);
      } catch (error: any) {
        if (error.code === 'functions/failed-precondition') {
          setBookingData(null);
          setErrorMessage("This business has disabled their booking site.");
        } else {
          console.error('Error fetching booking data:', error);
          setErrorMessage("Business not found or an unexpected error occurred.");
        }
      } finally {
        setLoading(false);
      }
    }

    if (slug) loadBookingData();
  }, [slug, applyThemeFromData]);



  if (loading) {
    return (
      <>
        <NavBar />
        <Box p={4} maxWidth="600px" mx="auto">
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (!bookingData) {
    return (
      <>
        <NavBar />
        <Box p={4} maxWidth="600px" mx="auto">
          <Typography variant="h6">
            {errorMessage || "Business not found"}
          </Typography>
        </Box>
      </>
    );
  }

  const workDays = bookingData.workSchedule
    .filter((day) => day.isWorkDay)
    .map((day) => day.workDay);

  return (
    <>
      <NavBar title={bookingData.businessName} showSectionNav={newVersion} />

      <Box sx={{ flex: 1 }}>
        { newVersion ? (
          <>
          <BusinessAboutSection
            businessName={bookingData.businessName}
            logoUrl={bookingData.logoData?.logoUrl || null}
            businessDescription={bookingData.businessDescription}
            businessImages={bookingData.businessImages}
          />
          <Divider />
          </>
        ) : (
          <BusinessAboutSectionOriginal
            businessName={bookingData.businessName}
            contactName={bookingData.contactName}
            phoneNumber={bookingData.phoneNumber}
          />
        )}

        <Divider />

        {/* Book Section */}
        <BusinessBookSection
          businessId={bookingData.businessId}
          businessName={bookingData.businessName}
          services={bookingData.services}
          workDays={workDays}
          workSchedule={bookingData.workSchedule}
          notesField={bookingData.notesField}
        />

        {newVersion && (
          <>
            <Divider />
            <BusinessContactSection
              businessId={bookingData.businessId}
              contactName={bookingData.contactName}
              phoneNumber={bookingData.phoneNumber} />
          </>
        )}
      </Box>
    </>
  );
}
